import { Badge, Button, Icon, IconButton } from '@ds/core';
import { Input, Select } from '@ds/forms';
import { ACOES_DO_FLUXO, CAMPOS_POR_GATILHO, OPERADORES, acaoDoFluxo, descricaoDoPasso } from '@/data/fluxos';
import type { PassoDeAcao, PassoDeCondicao, PassoDoFluxo } from '@/data/fluxos';
import type { GatilhoDeAutomacao } from '@/data/tipos';

/**
 * O painel de propriedades do passo selecionado no desenho.
 *
 * Fica fora do canvas de propósito: o desenho mostra a ORDEM e o que cada passo
 * faz; o detalhe se edita aqui, com rótulo, dica e teclado — coisas que um
 * cartão de 220px não comporta sem virar formulário espremido.
 */
export function EditorDePasso({ passo, gatilho, indice, total, onMudar, onMover, onRemover }: {
    passo: PassoDoFluxo;
    gatilho: GatilhoDeAutomacao;
    indice: number;
    total: number;
    onMudar: (mudanca: Partial<PassoDoFluxo>) => void;
    /** Reordenar sem arrastar: o mouse não pode ser o único caminho. */
    onMover: (direcao: -1 | 1) => void;
    onRemover: () => void;
}) {
    const descricao = descricaoDoPasso(passo.tipo);

    return (
        <div
            style={{
                padding: 14, border: '1px solid var(--border)', borderRadius: 'var(--radius-md)',
                display: 'flex', flexDirection: 'column', gap: 10, background: 'var(--surface)',
            }}
        >
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <Icon name={descricao?.icone ?? 'circle'} size={15} />
                <strong style={{ fontSize: 13, fontWeight: 600 }}>{descricao?.rotulo}</strong>
                {descricao?.bloqueio && <Badge tone="warning">não roda ainda</Badge>}
                <span style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 2 }}>
                    <IconButton
                        icon={<Icon name="chevron-up" size={14} />} size="sm"
                        label={`Subir ${descricao?.rotulo ?? passo.tipo}`}
                        disabled={indice === 0} onClick={() => onMover(-1)}
                    />
                    <IconButton
                        icon={<Icon name="chevron-down" size={14} />} size="sm"
                        label={`Descer ${descricao?.rotulo ?? passo.tipo}`}
                        disabled={indice >= total - 1} onClick={() => onMover(1)}
                    />
                    <Button size="sm" variant="ghost" onClick={onRemover}>Remover passo</Button>
                </span>
            </div>

            {descricao?.bloqueio && (
                <p style={{ margin: 0, fontSize: 12.5, color: 'var(--text-secondary)' }}>{descricao.bloqueio}</p>
            )}

            {passo.tipo === 'condicao' && (
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 8 }}>
                    <Select
                        aria-label="Campo" value={(passo as PassoDeCondicao).campo}
                        onChange={(v) => onMudar({ campo: v } as Partial<PassoDoFluxo>)}
                        options={(CAMPOS_POR_GATILHO[gatilho] ?? []).map((c) => ({ value: c.campo, label: c.rotulo }))}
                        size="sm"
                    />
                    <Select
                        aria-label="Operador" value={(passo as PassoDeCondicao).operador}
                        onChange={(v) => onMudar({ operador: v } as Partial<PassoDoFluxo>)}
                        options={OPERADORES.map((o) => ({ value: o.id, label: o.rotulo }))}
                        size="sm"
                    />
                    <Input
                        aria-label="Valor" value={(passo as PassoDeCondicao).valor}
                        onChange={(v) => onMudar({ valor: v } as Partial<PassoDoFluxo>)}
                        size="sm" placeholder="120"
                    />
                </div>
            )}

            {passo.tipo === 'acao' && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                    <Select
                        aria-label="Ação" value={(passo as PassoDeAcao).acao}
                        onChange={(v) => onMudar({ acao: v, campos: {} } as Partial<PassoDoFluxo>)}
                        options={ACOES_DO_FLUXO.map((a) => ({ value: a.id, label: a.rotulo }))}
                        size="sm"
                    />
                    {acaoDoFluxo((passo as PassoDeAcao).acao)?.bloqueio && (
                        <span style={{ fontSize: 12, color: 'var(--text-secondary)' }}>
                            {acaoDoFluxo((passo as PassoDeAcao).acao)?.bloqueio}
                        </span>
                    )}
                    {(acaoDoFluxo((passo as PassoDeAcao).acao)?.campos ?? []).map((campo) => (
                        <Input
                            key={campo} aria-label={campo} size="sm"
                            value={(passo as PassoDeAcao).campos[campo] ?? ''}
                            placeholder={campo === 'titulo' ? 'Ligar para {nome}, sumiu há {diasSemRetorno} dias' : campo}
                            onChange={(v) => onMudar({
                                campos: { ...(passo as PassoDeAcao).campos, [campo]: v },
                            } as Partial<PassoDoFluxo>)}
                        />
                    ))}
                    <span style={{ fontSize: 11.5, color: 'var(--text-tertiary)' }}>
                        Use <code>{'{campo}'}</code> para falar do caso: {'{nome}'}, {'{diasSemRetorno}'}.
                    </span>
                </div>
            )}

            {passo.tipo === 'agente' && (
                <Input
                    aria-label="Instrução para o agente" size="sm"
                    value={passo.instrucao}
                    placeholder="Escreva a mensagem de reativação para este paciente"
                    onChange={(v) => onMudar({ instrucao: v } as Partial<PassoDoFluxo>)}
                />
            )}

            {passo.tipo === 'espera' && (
                <Input
                    aria-label="Dias de espera" type="number" size="sm"
                    value={String(passo.dias)}
                    onChange={(v) => onMudar({ dias: Number(v) || 0 } as Partial<PassoDoFluxo>)}
                />
            )}
        </div>
    );
}
